/**
 * Preferences component
 * User preferences panel for controlling app settings and account options
 * Includes theme toggle, profession selection, and logout functionality
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { FiSun, FiMoon, FiChevronDown } from 'react-icons/fi';
import { IoIosLogOut } from 'react-icons/io';
import { MdWork } from 'react-icons/md';
import { IoCheckmark } from 'react-icons/io5';
import { closePreferences } from './actions';
import { services } from '../../api';
import { updateProfession as updateProfessionAction } from '../../screens/auth/login/reducer';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { setLocalData } from '../../utils/storage';
import { keys } from '../../api/keys';
import { toggleDarkMode } from 'screens/content/settings/action';

/**
 * Semi-transparent overlay for the preferences modal
 * Controls visibility with smooth transitions
 */
const ModalOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  padding: 1rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  transition: opacity 0.2s ease;
`;

/**
 * Container for the preferences modal content
 * Animates scale and opacity based on visibility
 */
const ModalContent = styled.div<{ isVisible: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.homeBackground};
  border-radius: 16px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: visible;
  width: 96%;
  max-width: 500px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  transform: scale(${({ isVisible }) => (isVisible ? 1 : 0.95)});
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: all 0.2s ease;
`;

/**
 * Section container within the preferences modal
 * Separated by borders
 */
const Section = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.border}33;

  &:last-child {
    border-bottom: none;
  }
`;

/**
 * Title for each section in the preferences modal
 */
const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.text.light};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

/**
 * Button for preference options like theme toggle and logout
 */
const OptionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${({ theme }) => theme.text.light}11;
  border: 1px solid ${({ theme }) => theme.border}33;
  border-radius: 8px;
  color: ${({ theme }) => theme.text.light};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.75rem;

  &:hover {
    background: ${({ theme }) => theme.text.light}22;
  }

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    font-size: 1.25rem;
  }
`;

/**
 * Descriptive text for preference sections
 */
const Description = styled.p`
  color: ${({ theme }) => theme.text.light}99;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

/**
 * Container for the profession dropdown
 */
const ProfessionDropdown = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
`;

/**
 * Button that opens the profession dropdown
 */
const ProfessionButton = styled.button<{ isSelected?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: ${({ theme }) => theme.text.light}11;
  border: 1px solid ${({ theme }) => theme.border}33;
  border-radius: 8px;
  color: ${({ theme }) => theme.text.light};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.text.light}22;
  }

  .right-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .chevron {
    transition: transform 0.2s ease;
    transform: ${({ isSelected }) => (isSelected ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

/**
 * Dropdown content container for profession options
 */
const DropdownContent = styled(motion.div)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background: ${({ theme }) => theme.homeBackground};
  border: 1px solid ${({ theme }) => theme.border}33;
  border-radius: 8px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  z-index: 1000;

  @media (prefers-color-scheme: dark) {
    background: ${({ theme }) => theme.sidebar.background};
    border: 1px solid ${({ theme }) => theme.border}33;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  }
`;

/**
 * Individual profession option in the dropdown
 */
const ProfessionOption = styled.button<{ isSelected?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${({ theme, isSelected }) => (isSelected ? theme.primary : 'transparent')};
  border: none;
  color: ${({ theme, isSelected }) => (isSelected ? '#fff' : theme.text.light)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme, isSelected }) => (isSelected ? theme.primary : theme.text.light + '11')};
  }

  svg {
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
    transform: scale(${({ isSelected }) => (isSelected ? 1 : 0.5)});
    transition: all 0.2s ease;
  }
`;

/**
 * Loading indicator for async operations
 */
const LoadingRing = styled(motion.div)`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.text.light}33;
  border-top: 2px solid ${({ theme }) => theme.text.light};
  border-radius: 50%;
`;

/**
 * Interface for the Preferences component props
 */
interface PreferencesProps {
  onLogoutPressed: () => void;
}

/**
 * Preferences component that shows user preference options
 * Includes theme toggle, profession selection, and logout
 *
 * @param onLogoutPressed - Callback function when user logs out
 * @returns Modal with user preference controls
 */
const Preferences: React.FC<PreferencesProps> = ({ onLogoutPressed }) => {
  const dispatch = useDispatch();
  const { isVisible } = useSelector((state: RootState) => state.preferences);
  const { isDarkMode } = useSelector((state: RootState) => state.settings);
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    closePreferences();
  };

  const handleThemeToggle = () => {
    toggleDarkMode({ isDarkMode: !isDarkMode });
  };

  const handleProfessionChange = async (professionId: number) => {
    setIsDropdownOpen(false);
    if (professionId === currentUser?.user.profession_id) return;

    try {
      setIsLoading(true);
      await services.auth.updateProfession({ profession_id: professionId });
      dispatch(updateProfessionAction(professionId));
      localStorage.setItem('selectedProfession', professionId.toString());
      // Update the user in localStorage using encrypted storage
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          user: {
            ...currentUser.user,
            profession_id: professionId,
          },
        };

        await setLocalData(keys.CURRENT_USER, updatedUser);
      }
      try {
        const response = await services.tabs.getAll();
        if (response.code === 200) {
          toast.success('Profession and tabs updated successfully');
        }
      } catch (error) {
        console.error('Error refreshing tabs:', error);
        toast.error('Profession updated but failed to refresh tabs. Please reload the page.');
      }
    } catch (error) {
      toast.error('Failed to update profession. Please try again.');
      setIsDropdownOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.stopPropagation();
    closePreferences();
    onLogoutPressed();
  };

  const currentProfession = currentUser?.professions?.find((p) => p.id === currentUser.user.profession_id);

  return (
    <ModalOverlay isVisible={isVisible} onClick={handleClose}>
      <ModalContent isVisible={isVisible} onClick={(e) => e.stopPropagation()}>
        <Section>
          <SectionTitle>Appearance</SectionTitle>
          <OptionButton onClick={handleThemeToggle}>
            {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
            <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </OptionButton>
        </Section>

        <Section>
          <SectionTitle>Manage Account</SectionTitle>
          <Description>Update your professional information and manage your account settings.</Description>

          <ProfessionDropdown>
            <ProfessionButton
              onClick={() => !isLoading && setIsDropdownOpen(!isDropdownOpen)}
              isSelected={isDropdownOpen}
            >
              <span>{currentProfession?.name || 'Select Profession'}</span>
              <div className="right-content">
                {isLoading ? (
                  <LoadingRing
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                ) : (
                  <>
                    <MdWork size={20} />
                    <FiChevronDown size={20} className="chevron" />
                  </>
                )}
              </div>
            </ProfessionButton>

            <AnimatePresence>
              {isDropdownOpen && (
                <DropdownContent
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {currentUser?.professions?.map((profession) => (
                    <ProfessionOption
                      key={profession.id}
                      isSelected={profession.id === currentUser.user.profession_id}
                      onClick={() => handleProfessionChange(profession.id)}
                      disabled={isLoading}
                    >
                      <IoCheckmark size={18} />
                      <span>{profession.name}</span>
                    </ProfessionOption>
                  ))}
                </DropdownContent>
              )}
            </AnimatePresence>
          </ProfessionDropdown>

          <Description>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            Logging out will end your current session. You'll need to sign in again to access your account.
          </Description>

          <OptionButton onClick={handleLogout}>
            <IoIosLogOut size={20} style={{ transform: 'rotate(180deg)' }} />
            <span>Log Out</span>
          </OptionButton>
        </Section>
      </ModalContent>
    </ModalOverlay>
  );
};

export default Preferences;
